import React, { useState, useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTheme } from '@mui/material/styles';
import {Box, Card, Divider, List, ListItem, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery} from '@mui/material';
import Grid from '@mui/material/Grid2';

import Main from '../../../../layouts/Main';
import Container from '../../../../components/Container';

import FilingSchedule from '../../../../types/filers/fillingSchedule';
import LegislativeCandidates from './components/LegislativeCandidates';
import NonLegislativeCandidates from './components/NonLegislativeCandidates';
import BallotQuestionCommittee from './components/BallotQuestionCommittee';
import IndependentExpenditure from './components/IndependentExpenditure';
import ElectioneeringCommunication from './components/ElectioneeringCommunication';
import LocalPartyCommittee from './components/LocalPartyCommittee';
import LocalCandidate from './components/LocalCandidate';
import IEPACommittee from './components/IEPACommittee';
import PaCommittee from './components/PACommittee';
import StatePartyCommittee from './components/StatePartyCommittee';


const types = [
    {
      id: 'legislative',
      title: 'Legislative Candidates',
      shortTitle: 'Legislative',
    },
    {
      id: 'nonLegislative',
      title: 'Non-Legislative Candidates',
      shortTitle: 'Non-Legislative',
    },
    {
      id: 'local',
      title: 'Local Candidates',
      shortTitle: 'Local Candidates',
    },
    {
      id: 'pac',
      title: 'Political Action Committees',
      shortTitle: 'PACs',
    },
    {
      id: 'state',
      title: 'State Party Committees',
      shortTitle: 'State Party',
    },
    {
      id: 'lpc',
      title: 'Local Party Committees',
      shortTitle: 'Local Party',
    },
    {
        id: 'bq',
        title: 'Ballot Question Reporting',
        shortTitle: 'Ballot Questions',
    },
    {
      id: 'iepac',
      title: 'Independent Expenditure PACs',
      shortTitle: 'IEPACs',
  },
    {
        id: 'ie',
        title: 'Independent Expenditures',
        shortTitle: 'Independent Exps',
    },
    {
        id: 'ec',
        title: 'Electioneering Communications',
        shortTitle: 'Electioneering Communication',
    },
  
  ];

const FilerTypes = (): JSX.Element => {

    document.title = 'OCPF - Filer Types & Filing Schedules';

    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.down('md'));
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const { showBoundary } = useErrorBoundary();  

    const [activeType, setActiveType] = useState('legislative');
    const [legislativeSchedules, setLegislativeSchedules] = useState<FilingSchedule[]>();
    const [lpcSchedules, setLpcSchedules] = useState<FilingSchedule[]>();
    const [BallotQuestionSchedules, setBallotQuestionSchedules] = useState<FilingSchedule[]>();
    const [statePartySchedules, setStatePartySchedules] = useState<FilingSchedule[]>();
    const [previouslegislativeSchedules, setPreviousLegislativeSchedules] = useState<FilingSchedule[]>();
    const [previousLpcSchedules, setPreviousLpcSchedules] = useState<FilingSchedule[]>();
    const [previousBallotQuestionSchedules, setPreviousBallotQuestionSchedules] = useState<FilingSchedule[]>();

    const[sPCLateFilingSchedule, setSPCLateFilingSchedule] = useState('');
    const[legLateFilingSchedule, setLegLateFilingSchedule] = useState('');

    useEffect(() => {
        getFilingSchedules();
      }, []);

    const getFilingSchedules = async () => {

        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/filingSchedules`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the filing schedules.'));
            }
            return result.json();
          })
          .then(result => {
            setLegislativeSchedules(result.legislativeSchedule);
            setLpcSchedules(result.lpcSchedule);
            setBallotQuestionSchedules(result.bqSchedule);
            setStatePartySchedules(result.statePartySchedule);
            if(result.previousYearSchedules){
              setPreviousLegislativeSchedules(result.previousYearSchedules.legislativeSchedule);
              setPreviousLpcSchedules(result.previousYearSchedules.lpcSchedule);
              setPreviousBallotQuestionSchedules(result.previousYearSchedules.bqSchedule);
              if(result.previousYearSchedules.statePartySchedule.length > 0)
                setSPCLateFilingSchedule(result.previousYearSchedules.statePartySchedule[0].lateContributionSummary);
              if(result.previousYearSchedules.legacyDepositorySchedule.length > 0)
                setLegLateFilingSchedule(result.previousYearSchedules.legacyDepositorySchedule[0].lateContributionSummary);
            }
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }

    return (
        <Main>
        <Container maxWidth={{ sm: 720, md: 1440 }}>
         <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            sx={{
                fontWeight: 700,
            }}
            paddingTop={0.5}
            gutterBottom
            marginBottom={2}
            >
            Filer Types & Filing Schedules
        </Typography>
        <Divider/>
        <Grid container spacing={4} marginTop={4}>
          {
            !isSM &&
            <Grid size={{xs:12, md:2.7}}>
                <Card sx={{ boxShadow: 3 }}>
                  <List
                  disablePadding
                  sx={{
                    display: { xs: 'inline-flex', md: 'flex' },
                    flexDirection: 'column',
                    overflow: 'auto',
                    width: '100%',
                    paddingY: 2,
                    paddingX: 0,
                  }}
                >
                {types.map((item) => (
                  <ListItem
                    key={item.id}
                    onClick={() => setActiveType(item.id)}
                    disableGutters
                    sx={{
                      marginRight: 0,
                      cursor: 'pointer',
                      flex: 0,
                      paddingX: 3,
                      borderLeft: {
                        xs: 'none',
                        md: '2px solid transparent',
                      },
                      borderLeftColor: {
                        md:
                          activeType === item.id
                            ? theme.palette.primary.main
                            : 'transparent',
                      },
                      boxShadow: { 
                        xs: 0, 
                        md: activeType === item.id ? 3 : 0 
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      noWrap
                      fontWeight={
                        activeType === item.id ? 550 : 400
                      }
                      color={
                        activeType === item.id
                          ? 'primary'
                          : 'text.main'
                      }
                    >
                       {item.title}
                    </Typography>
                  </ListItem>
                ))}
                 <ListItem
                    onClick={() => {
                        setActiveType("Special");
                        window.location.href = '/Reports/SpecialElections';
                    }}
                    disableGutters
                    sx={{
                      marginRight: { xs: 2, md: 0 },
                      cursor: 'pointer',
                      flex: 0,
                      paddingX: { xs: 0, md: 3 },
                      borderLeft: {
                        xs: 'none',
                        md: '2px solid transparent',
                      }
                    }}
                  >
                    <Typography variant="subtitle1" noWrap color={'text.main'}>
                       {isMd ? "Special" : "Special Elections"}
                    </Typography>
                  </ListItem>
                </List>
              </Card>
            </Grid>
          }
          {
            isSM &&
            <Grid size={12} display={'flex'} flexWrap={'wrap'}>
              <ToggleButtonGroup
                exclusive
                fullWidth = {true}
                aria-label="filer types"
                size="small"
                color="primary"
                value={activeType}
            >
                {types.slice(0,3).map((type) => (
                    <ToggleButton 
                        key={type.id} 
                        value={type.id} 
                        aria-label={`filer data - ${type}`}
                        sx={{fontWeight:800, width: "auto"}}
                        onClick={() => setActiveType(type.id)}
                    >
                        {type.shortTitle}
                    </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <ToggleButtonGroup
                 exclusive
                 fullWidth = {isMd? false: true}
                 aria-label="filer content types"
                 size="small"
                 color="primary"
                 value={activeType}
            >
                {types.slice(3,7).map((type) => (
                    <ToggleButton key={type.id} value={type.id} aria-label={`filer data - ${type}`}
                        sx={{fontWeight:800, width: "auto"}}
                        onClick={() => setActiveType(type.id)}
                    >
                        {type.shortTitle}
                    </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <ToggleButtonGroup
                 exclusive
                 fullWidth = {isMd? false: true}
                 aria-label="filer content types"
                 size="small"
                 color="primary"
                 value={activeType}
              >
                {types.slice(7).map((type) => (
                    <ToggleButton key={type.id} value={type.id} aria-label={`filer data - ${type}`}
                        sx={{fontWeight:800, width: "auto"}}
                        onClick={() => setActiveType(type.id)}
                    >
                        {type.shortTitle}
                    </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          }
         
          <Grid size={{xs:12, md:9.3}}>
            <Card sx={{ boxShadow: 3, padding: 4 }}>
                {
                    activeType === 'legislative' && <LegislativeCandidates schedule={legislativeSchedules} previousSchedule={previouslegislativeSchedules}></LegislativeCandidates>
                }
                {
                    activeType === 'nonLegislative' && <NonLegislativeCandidates lateContributionSummary={legLateFilingSchedule}></NonLegislativeCandidates>
                }     
                {
                  activeType === 'pac' && <PaCommittee></PaCommittee>
                }
                {
                    activeType === 'state' && <StatePartyCommittee lateContributionSummary={sPCLateFilingSchedule}></StatePartyCommittee>
                }
                {
                    activeType === 'local' && <LocalCandidate></LocalCandidate>
                }     
                {
                    activeType === 'ie' && <IndependentExpenditure></IndependentExpenditure>
                }   
                {
                  activeType === 'iepac' && <IEPACommittee></IEPACommittee>
                }
                {
                    activeType === 'lpc' && <LocalPartyCommittee schedule={lpcSchedules} previousSchedule={previousLpcSchedules}></LocalPartyCommittee>
                }  
                {
                    activeType === 'bq' && <BallotQuestionCommittee schedule={BallotQuestionSchedules} previousSchedule={previousBallotQuestionSchedules}></BallotQuestionCommittee>
                }  
                {
                    activeType === 'ec' && <ElectioneeringCommunication></ElectioneeringCommunication>
                }             
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Main>
  );
};

export default FilerTypes;
